/**
 * HLOS-168-B + Hotfix Safari: Vault backend "indexeddb" — fallback para
 * quando OPFS está bloqueado (Safari em certos contextos, iOS WebView
 * antigos, etc.).
 *
 * Layout (mesmo do OPFSBackend):
 *   idb://<userId>/<file>  → bytes cifrados
 *
 * O backend é STATELESS — sem cache interna. Cada operação é uma chamada
 * direta ao IndexedDB API. VaultStore pode cachear resultado se precisar.
 *
 * Por que IndexedDB como fallback:
 * - Suportado em TODOS os browsers modernos (incluindo Safari iOS)
 * - Não precisa de permissão especial (diferente de OPFS em alguns contexts)
 * - Persiste entre sessões
 * - Já está disponível no Safari (não trava como `getDirectory()`)
 *
 * Constitucional:
 * - CL-07 (vault local-first): IndexedDB é local no app sandbox do browser.
 * - CL-08 (zero key escrow): bytes são SEMPRE cifrados antes de chegar aqui.
 * - Não toca LLM, LATENT_DIM, P=1000, etc.
 */

import type { VaultStorage } from "../store";

/**
 * Wrapper minimal de Promise para callbacks do IndexedDB.
 */
function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB error"));
  });
}

function txToPromise(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB tx error"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB tx aborted"));
  });
}

/**
 * Implementação IndexedDB do VaultStorage. Hotfix para Safari/iOS onde
 * OPFS está bloqueado.
 */
export class IndexedDBBackend implements VaultStorage {
  private readonly dbName = "hlos-vault";
  private readonly storeName = "files";
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Abre (ou cria) o banco IndexedDB.
   * Cache do promise para evitar múltiplas aberturas.
   */
  private getDB(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(this.dbName, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error("IndexedDB open error"));
    });
    return this.dbPromise;
  }

  /**
   * Sanitiza path (anti `..` injection).
   */
  private sanitize(relativePath: string): string | null {
    if (relativePath.includes("..") || relativePath.startsWith("/")) {
      return null;
    }
    const parts = relativePath.split("/").filter((p) => p.length > 0);
    if (parts.length === 0) return null;
    return parts.join("/");
  }

  async read(relativePath: string): Promise<Uint8Array | null> {
    const key = this.sanitize(relativePath);
    if (!key) return null;
    try {
      const db = await this.getDB();
      const tx = db.transaction(this.storeName, "readonly");
      const store = tx.objectStore(this.storeName);
      const value = await reqToPromise(store.get(key));
      if (value === undefined) return null;
      return value instanceof Uint8Array
        ? value
        : new Uint8Array(value as ArrayBuffer);
    } catch {
      return null;
    }
  }

  async write(relativePath: string, data: Uint8Array): Promise<void> {
    const key = this.sanitize(relativePath);
    if (!key) {
      throw new Error(`IndexedDBBackend.write: invalid path "${relativePath}"`);
    }
    const db = await this.getDB();
    const tx = db.transaction(this.storeName, "readwrite");
    const store = tx.objectStore(this.storeName);
    store.put(data, key);
    await txToPromise(tx);
  }

  async list(directory: string): Promise<string[]> {
    const dir = this.sanitize(directory + "/dummy"); // sanitize via path
    if (!dir) return [];
    const prefix = directory.endsWith("/") ? directory : directory + "/";
    try {
      const db = await this.getDB();
      const tx = db.transaction(this.storeName, "readonly");
      const store = tx.objectStore(this.storeName);
      const allKeys = await reqToPromise(store.getAllKeys());
      const files: string[] = [];
      for (const key of allKeys) {
        if (typeof key === "string" && key.startsWith(prefix)) {
          files.push(key.slice(prefix.length));
        }
      }
      return files.sort();
    } catch {
      return [];
    }
  }

  async remove(relativePath: string): Promise<void> {
    const key = this.sanitize(relativePath);
    if (!key) return;
    try {
      const db = await this.getDB();
      const tx = db.transaction(this.storeName, "readwrite");
      const store = tx.objectStore(this.storeName);
      store.delete(key);
      await txToPromise(tx);
    } catch {
      // Ignorar erros (not found, etc)
    }
  }
}
